import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TownshipService } from '../../../services/township.service';
import { TownshipFull, TownshipStatus } from '../../../models/township.models';
import { httpErrorMessage } from '../../../core/http-error';

const STATUSES: TownshipStatus[] = ['created', 'active', 'inactive'];

@Component({
  selector: 'app-township-detail',
  imports: [DatePipe, RouterLink, FormsModule],
  templateUrl: './township-detail.component.html',
  styleUrl: './township-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TownshipDetailComponent implements OnInit {
  private readonly townshipService = inject(TownshipService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly statuses = STATUSES;

  readonly township = signal<TownshipFull | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  /** Editable copies. */
  readonly name = signal('');
  readonly status = signal<TownshipStatus>('created');
  readonly saving = signal(false);
  readonly deleting = signal(false);
  readonly actionError = signal<string | null>(null);

  private id = '';

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.townshipService.getTownship(this.id).subscribe({
      next: t => {
        this.applyTownship(t);
        this.loading.set(false);
      },
      error: err => {
        this.error.set(httpErrorMessage(err, 'Failed to load township.'));
        this.loading.set(false);
      },
    });
  }

  save(): void {
    this.saving.set(true);
    this.actionError.set(null);
    this.townshipService
      .updateTownship(this.id, { name: this.name(), status: this.status() })
      .subscribe({
        next: t => {
          this.applyTownship(t);
          this.saving.set(false);
        },
        error: err => {
          this.actionError.set(httpErrorMessage(err, 'Failed to save changes.'));
          this.saving.set(false);
        },
      });
  }

  remove(): void {
    if (!confirm('Delete this township? This cannot be undone.')) return;
    this.deleting.set(true);
    this.actionError.set(null);
    this.townshipService.deleteTownship(this.id).subscribe({
      next: () => this.router.navigate(['/townships']),
      error: err => {
        this.actionError.set(httpErrorMessage(err, 'Failed to delete township.'));
        this.deleting.set(false);
      },
    });
  }

  private applyTownship(t: TownshipFull): void {
    this.township.set(t);
    this.name.set(t.name ?? '');
    this.status.set(t.status);
  }
}
